import { useTranslation } from "react-i18next";
import type { BlobItem } from "@azure/storage-blob";
import { Button } from "@/components/ui/button";
import { FileText, Download, AlertCircle } from "lucide-react";
import { Loading } from "@/components/ui/loading";
import type { UseBlobViewerReturn } from "@/hooks/dashboard/useBlobViewer";

interface BlobContentRendererProps {
  blob: BlobItem | null;
  blobViewer: UseBlobViewerReturn;
}

export function BlobContentRenderer({ blob, blobViewer }: BlobContentRendererProps) {
  const { t } = useTranslation();

  const {
    blobContent,
    loading,
    error,
    docxHtml,
    processingDocx,
    isTextFile,
    isPdfFile,
    isDocxFile,
    handleDownload,
  } = blobViewer;

  if (loading || processingDocx) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  if (!blobContent) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <FileText className="w-5 h-5 mr-2" />
        <span>{t("common.noContentToDisplay")}</span>
      </div>
    );
  }

  const { content, contentType } = blobContent;

  // Handle PDF files
  if (isPdfFile(contentType) && typeof content === 'string') {
    return (
      <div className="w-full h-96">
        <iframe
          src={content}
          className="w-full h-full border rounded"
          title={`PDF viewer for ${blob?.name}`}
        />
      </div>
    );
  }

  // Handle text files, JSON, and Markdown files
  if (isTextFile(contentType, blob?.name || '') && typeof content === 'string') {
    return (
      <div className="max-h-96 overflow-auto">
        <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded border">
          {content}
        </pre>
      </div>
    );
  }

  // Handle DOCX files
  if (isDocxFile(contentType, blob?.name || '')) {
    if (docxHtml) {
      return (
        <div className="max-h-96 overflow-auto">
          <div
            className="prose max-w-none p-4 bg-white rounded border"
            dangerouslySetInnerHTML={{ __html: docxHtml }}
          />
        </div>
      );
    } else {
      // Show processing or error state
      return (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <FileText className="w-12 h-12 text-gray-400" />
          <div className="text-center">
            <p className="font-medium">Microsoft Word Document</p>
            <p className="text-sm text-gray-500">
              {processingDocx ? t("common.processingDocument") : t("common.documentProcessingFailed")}
            </p>
          </div>
          <Button onClick={handleDownload} className="mt-4">
            <Download className="w-4 h-4 mr-2" />
            {t("common.downloadDocument")}
          </Button>
        </div>
      );
    }
  }

  // For other file types, show a generic message
  return (
    <div className="flex flex-col items-center justify-center py-8 space-y-4">
      <FileText className="w-12 h-12 text-gray-400" />
      <div className="text-center">
        <p className="font-medium">{contentType}</p>
        <p className="text-sm text-gray-500">
          {t("common.previewNotAvailable")}
        </p>
      </div>
      <Button onClick={handleDownload} className="mt-4">
        <Download className="w-4 h-4 mr-2" />
        {t("common.downloadFile")}
      </Button>
    </div>
  );
}
